import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {FormBuilder, NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {distinctUntilChanged, of, switchMap} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {filter, map} from "rxjs/operators";
import {NgIf} from '@angular/common';
import {MultiSelectWithServiceComponent} from '../../multi-select-with-service/multi-select-with-service.component';
import {TableDisplayConfig} from '../../../entities/table-display-config.entity';
import {ServiceProviderHub} from '@generated-backend-types';
import {TableFilterFavoritesService} from '../../../services/table-filter-favorites/table-filter-favorites.service';
import {FormErrorService} from '../../../services/form-error-service/form-error.service';
import {NotEnoughPermissionsError} from '../../../errors/NotEnoughPermissionsError';
import TableFilterFavorite = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Meta.TableFilterFavorite;
import TableFilterFavoriteData = ServiceProviderHub.App.Data.ServiceProviderHub.Settings.TableFilterFavoriteData;

@Component({
  selector: 'app-table-filter-loader',
  templateUrl: './table-filter-loader.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [MultiSelectWithServiceComponent, ReactiveFormsModule, NgIf, TranslatePipe],
  standalone: true
})
export class TableFilterLoaderComponent implements OnInit {
  protected cdr = inject(ChangeDetectorRef);
  protected translate = inject(TranslateService);
  readonly tableFilterFavoriteService = inject(TableFilterFavoritesService);
  private readonly formErrorService = inject(FormErrorService);
  private readonly toastrService = inject(ToastrService);

  @Output() public tableDisplayConfigChange: EventEmitter<TableDisplayConfig> = new EventEmitter<TableDisplayConfig>();
  @Output()
  public openSorter = new EventEmitter<void>
  public reloadFilters = new EventEmitter<void>
  @Input() showFavorites = true;
  @Input() showToggleButtons = true;
  @Output() public showFiltersChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public resetFilter: EventEmitter<void> = new EventEmitter<void>();
  public selectedFilterChanged = new EventEmitter<null | string | TableFilterFavorite[]>();
  private readonly fb = inject(NonNullableFormBuilder);
  public form2 = this.fb.group({
    selectedFavorite: this.fb.control<TableFilterFavorite[] | undefined>(undefined),
  })
  private readonly nfb = inject(FormBuilder);
  public form = this.fb.group({
    id: this.fb.control<string | undefined>(undefined),
    title: this.fb.control('', Validators.required), // filled in view
    filter: this.fb.control([]),
    column_order: this.nfb.control<string | null>(''),
    sorts: this.fb.control(''),
    per_page: this.fb.control(30),
    hidden_columns: this.fb.control(''),
    table_id: this.fb.control(''),
  })

  private _showFilters = false;

  public get showFilters(): boolean {
    return this._showFilters
  }

  @Input()
  public set showFilters(show: boolean) {
    this._showFilters = show;
    this.showFiltersChanged.emit(show);
  }

  public _tableDisplayConfig?: TableDisplayConfig;

  @Input()
  public set tableDisplayConfig(conf: TableDisplayConfig | undefined) {
    console.debug(conf?.favorite, this._tableDisplayConfig?.favorite);
    if (!conf) {
      return;
    }
    if (conf.favorite) {
      if (conf.favorite !== this._tableDisplayConfig?.favorite) {
        this.loadFavorite(conf.favorite)
      }


    } else {
      if (this.form2.value.selectedFavorite) {
        this.unloadFavorite();
      }

    }
    this._tableDisplayConfig = conf;
  }

  public toggleFilter() {

    this.showFilters = !this.showFilters;
  }

  public ngOnInit(): void {
    this.selectedFilterChanged.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      filter(f => typeof f !== "string"),
      map(f => f as TableFilterFavorite[] | null),
      map(f => f?.[0] ?? null)
    )
      .subscribe(
        (f: TableFilterFavorite | null) => {

          this.selectedFavorite(f)
        }
      )
  }

  public selectedFavorite(filterFavorite: TableFilterFavorite | null) {

    if (filterFavorite === null) {

      this.unloadFavorite()
      this.resetFilter.next()
      return;
    }

    this.form2.controls.selectedFavorite.setValue([filterFavorite]);

    this.form.controls.id.setValue(filterFavorite.id);
    this.form.controls.title.setValue(filterFavorite.title);

    const filters: { [param: string]: unknown } = {};

    Object.entries(filterFavorite.filter).forEach((i: string[]) => {
      if (i[1] !== null && i[1] !== undefined) filters[i[0]] = i[1];
    });

    const queryParams = {
      column_order: filterFavorite.column_order,
      favorite: filterFavorite.id,
      hidden_columns: filterFavorite.hidden_columns,
      per_page: filterFavorite.per_page,
      sorts: filterFavorite.sorts,
      filter: filters as unknown as { [param: string]: string; },
    }

    if (this._tableDisplayConfig) {
      this.tableDisplayConfigChange.emit({...this._tableDisplayConfig, ...queryParams})
    }
  }

  public save() {
    if (!this._tableDisplayConfig) {
      return;
    }


    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.form.controls.filter.setValue(this._tableDisplayConfig.filter)
    this.form.controls.column_order.setValue(this._tableDisplayConfig.column_order)
    this.form.controls.sorts.setValue(this._tableDisplayConfig.sorts)
    this.form.controls.per_page.setValue(this._tableDisplayConfig.per_page)
    this.form.controls.hidden_columns.setValue(this._tableDisplayConfig.hidden_columns ?? '')
    this.form.controls.table_id.setValue(this._tableDisplayConfig.tableConfig.id)


    this.form.updateValueAndValidity();
    this.cdr.markForCheck()
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }


    of(this.form.value).pipe(
      switchMap(data => {

        if (data.id) {
          return this.tableFilterFavoriteService.update(data.id, data as TableFilterFavoriteData);
        }

        return this.tableFilterFavoriteService.create(data as TableFilterFavoriteData);
      })
    ).subscribe({
      next: res => {


        this.toastrService.success(
          this.translate.instant('table_filter.saved_success')
        );
        this.reloadFilters.emit()
        this.selectedFavorite(res.data)
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('shared.misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(
            this.translate.instant(
              this.translate.instant('table_filter.saved_error')
            )
          );
        }
        this.cdr.markForCheck();
      }
    });
  }

  public deleteFavorite() {
    if (!this.form.controls.id.value) {
      return
    }
    this.tableFilterFavoriteService.delete(this.form.controls.id.value).subscribe(
      () => {
        this.selectedFavorite(null)
        this.reloadFilters.emit()
        this.toastrService.success(
          this.translate.instant('table_filter.deleted_success')
        );
      }
    )
  }

  private unloadFavorite() {
    this.form2.controls.selectedFavorite.reset(undefined);
    this.form.controls.id.reset(undefined);
    this.form.controls.title.setValue('');
  }

  private loadFavorite(id: string) {
    this.tableFilterFavoriteService.get(id).subscribe(
      (d) => {
        this.selectedFavorite(d.data)
      }
    )
  }

}
